/**
 * Modal pour créer ou éditer un template d'email
 */

import { useState, useEffect, useRef } from "react";
import { EmailTemplate, TEMPLATE_COLORS, TEMPLATE_VARIABLES } from "@/types/emailTemplates";
import { previewTemplate } from "@/lib/emailTemplateUtils";

interface TemplateModalProps {
  isOpen: boolean;
  template: EmailTemplate | null; // null = mode création, sinon mode édition
  onClose: () => void;
  onSave: (name: string, body: string, color: string) => Promise<void>;
}

export function TemplateModal({ isOpen, template, onClose, onSave }: TemplateModalProps) {
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [color, setColor] = useState("#3B82F6");
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Initialiser les valeurs si mode édition
  useEffect(() => {
    if (template) {
      setName(template.name);
      setBody(template.body);
      setColor(template.color);
    } else {
      setName("");
      setBody("");
      setColor("#3B82F6");
    }
    setShowPreview(false);
  }, [template, isOpen]);

  const handleInsertVariable = (variable: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newBody = body.substring(0, start) + variable + body.substring(end);
    
    setBody(newBody);
    
    // Replacer le curseur après la variable insérée
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + variable.length, start + variable.length);
    }, 0);
  };

  const handleSave = async () => {
    if (!name.trim() || !body.trim()) {
      // La validation sera gérée par le parent avec toast
      return;
    }

    setSaving(true);
    try {
      await onSave(name.trim(), body.trim(), color);
      onClose();
    } catch (err) {
      console.error("Erreur lors de la sauvegarde:", err);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {template ? "Modifier le template" : "Créer un nouveau template"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Nom du template */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nom du template *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Relance polie, Confirmation urgente..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              maxLength={50}
            />
            <div className="mt-1 text-xs text-gray-500">
              {name.length}/50 caractères
            </div>
          </div>

          {/* Couleur */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Couleur du bouton
            </label>
            <div className="flex flex-wrap gap-2">
              {TEMPLATE_COLORS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setColor(c.value)}
                  className={`w-10 h-10 rounded-md border-2 transition-all ${
                    color === c.value ? "border-gray-900 scale-110" : "border-transparent"
                  }`}
                  style={{ backgroundColor: c.value }}
                  title={c.label}
                />
              ))}
            </div>
          </div>

          {/* Corps du message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Corps du message *
            </label>
            <textarea
              ref={textareaRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Bonjour {clientFirstName},&#10;&#10;Votre événement au {centerName} est confirmé pour le {eventDate}.&#10;&#10;Cordialement,&#10;L'équipe Smile World"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
              rows={12}
            />
            
            {/* Boutons de variables */}
            <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-md">
              <div className="text-xs font-medium text-gray-700 mb-2">
                💡 Insérer une variable (cliquez pour ajouter au curseur) :
              </div>
              <div className="flex flex-wrap gap-1.5">
                {TEMPLATE_VARIABLES.map((variable) => (
                  <button
                    key={variable.key}
                    type="button"
                    onClick={() => handleInsertVariable(`{${variable.key}}`)}
                    className="inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-white text-gray-700 border border-gray-300 hover:bg-indigo-50 hover:border-indigo-300 hover:text-indigo-700 transition-colors"
                    title={`${variable.description} (ex: ${variable.example})`}
                  >
                    {variable.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Prévisualisation */}
          <div>
            <button
              type="button"
              onClick={() => setShowPreview(!showPreview)}
              className="text-sm font-medium text-indigo-600 hover:text-indigo-700 underline"
            >
              {showPreview ? "Masquer" : "Afficher"} la prévisualisation avec données d'exemple
            </button>
            
            {showPreview && body.trim() && (
              <div className="mt-3 p-4 bg-gray-50 border border-gray-200 rounded-md">
                <div className="text-xs font-medium text-gray-700 mb-2">
                  Aperçu du rendu final :
                </div>
                <div className="text-sm text-gray-900 whitespace-pre-wrap">
                  {previewTemplate(body)}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim() || !body.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Enregistrement..." : template ? "Modifier" : "Créer"}
          </button>
        </div>
      </div>
    </div>
  );
}

